import isNumber from "lodash/isNumber";
import { isFiniteNumber, isPositiveInteger } from "util/number-utils";

export const FRAME_INTERVAL_SECONDS = 1;
export const FRAMES_PER_LINE = 10;
export const FRAME_WIDTH = 160;
export const FRAME_HEIGHT = 90;

type FramePosition = { line: number; column: number; x: number; y: number };

export const getFramePosition = ({
    frameTime,
    frameInterval = FRAME_INTERVAL_SECONDS,
    framesPerLine = FRAMES_PER_LINE,
    frameWidth = FRAME_WIDTH,
    frameHeight = FRAME_HEIGHT
}: {
    frameTime?: number;
    frameInterval?: number;
    framesPerLine?: number;
    frameWidth?: number;
    frameHeight?: number;
}): FramePosition => {
    isPositiveInteger(`framesPerLine`, framesPerLine);
    isPositiveInteger(`frameWidth`, frameWidth);
    isPositiveInteger(`frameHeight`, frameHeight);

    if (!isFiniteNumber(frameInterval) || frameInterval < 0) {
        throw new RangeError(
            `[frameInterval ${frameInterval}] needs to be a positive finite number.`
        );
    }

    if (!isFiniteNumber(frameTime) || frameTime < 0) {
        throw new RangeError(
            `[frameTime ${frameTime}] needs to be a positive finite number.`
        );
    }

    if (!isNumber(frameTime) || !Number.isFinite(frameTime) || frameTime < 0) {
        throw new RangeError(
            `[frameTime: ${frameTime}] needs to be a positive finite number.`
        );
    }

    // We round the time to the closest frame time available in the spritemap
    const closestMatchingTime =
        frameInterval * Math.round(frameTime / frameInterval);

    const number = closestMatchingTime / frameInterval;
    const line = Math.floor(number / framesPerLine);
    const column = number % framesPerLine;

    return {
        line,
        column,
        x: column * frameWidth,
        y: line * frameHeight
    };
};

export const renderSpriteFrames = ({
    canvas,
    sprite,
    from,
    to,
    height,
    width,
    y: renderY,
    frameInterval = FRAME_INTERVAL_SECONDS,
    framesPerLine = FRAMES_PER_LINE,
    frameWidth = FRAME_WIDTH,
    frameHeight = FRAME_HEIGHT
}: {
    canvas: HTMLCanvasElement;
    sprite: CanvasImageSource;
    from: number;
    to: number;
    height: number;
    width: number;
    y?: number;
    frameInterval?: number;
    framesPerLine?: number;
    frameWidth?: number;
    frameHeight?: number;
}): FramePosition[] => {
    if (!isFiniteNumber(from) || from < 0) {
        throw new RangeError(
            `[from ${from}] needs to be a positive finite number.`
        );
    }

    if (!isFiniteNumber(to) || to < 0) {
        throw new RangeError(
            `[to ${to}] needs to be a positive finite number.`
        );
    }

    if (to <= from) {
        throw new RangeError(
            `[from: ${from}] needs to be strictly greater than [to: ${to}]`
        );
    }

    const ctx = canvas.getContext(`2d`);

    const duration = to - from;

    // the frameHeight will always differ from the canvas we're trying to
    // render the frames into
    const scaleFactor = height / frameHeight;
    const scaledToCanvasFrameWidth = frameWidth * scaleFactor;

    // We create an array that picks all the best matching frames times based
    // on the `from` and `to` provided arguments
    const expectedNbOfFramesToDraw = Math.ceil(
        width / scaledToCanvasFrameWidth
    );
    const targetFrameInterval = duration / (expectedNbOfFramesToDraw || 1);

    let actualCanvasFrameWidth = scaledToCanvasFrameWidth;
    let frameOffset = 0;
    let nbOfFramesToDraw = Math.ceil(duration / frameInterval);
    let canvasFrameInterval = frameInterval;
    if (targetFrameInterval > frameInterval) {
        nbOfFramesToDraw = expectedNbOfFramesToDraw;
        // if we have 500px, we have to draw 7 thumbnails, which have are "80px" wide
        // We calculate than 500/7 = 71, and calculate the offset to "center" the frames
        actualCanvasFrameWidth = width / nbOfFramesToDraw;
        frameOffset = (scaledToCanvasFrameWidth - actualCanvasFrameWidth) / 2;
        canvasFrameInterval = targetFrameInterval;
    }

    const framesTimes = Array(nbOfFramesToDraw)
        .fill(0)
        .map((val, i) => from + canvasFrameInterval * i);

    const framesToDraw = framesTimes.map((time) =>
        getFramePosition({
            frameTime: time,
            frameInterval,
            framesPerLine,
            frameWidth,
            frameHeight
        })
    );

    // And we finally draw all of that in a canvas
    framesToDraw.forEach(({ x, y }, index) => {
        // quick math...
        const sourceX = x + frameOffset / scaleFactor;

        // depending on whether we have gaps or not
        // we draw images differently
        let targetX;
        let repeatFrame = 1;
        if (targetFrameInterval < frameInterval) {
            const widthBeforeNextFrame = (frameInterval / duration) * width;
            targetX = widthBeforeNextFrame * index;

            repeatFrame = Math.ceil(
                widthBeforeNextFrame / actualCanvasFrameWidth
            );
        } else {
            targetX = actualCanvasFrameWidth * index;
        }

        for (let i = 0; i < repeatFrame; i++) {
            ctx.drawImage(
                sprite,
                sourceX,
                y,
                actualCanvasFrameWidth / scaleFactor,
                frameHeight,
                targetX + i * actualCanvasFrameWidth,
                renderY,
                actualCanvasFrameWidth,
                height
            );
        }
    });

    return framesToDraw;
};
